import type {
  ApiClientDetail,
  ApiClientListItem,
  ApiOrganizationClientProfile,
  BusinessIdentityRequest,
  ClientByIdResponse,
  ClientListItem,
  CreateClientBody,
  IdentityType,
  IndividualIdentityRequest,
} from "../types/clients-api.types";

/**
 * The backend (qa) returns clients as the raw `ClientEntity` row with the shared `identity` relation joined:
 * tenant contact data at the root, legal data (names, document, KYC) nested under `identity`. For BUSINESS
 * identities the legal name is stored in `identity.first_name`.
 *
 * These normalizers flatten that shape into the view models the UI consumes, so components never deal with
 * the nesting (or with its absence when a relation is not joined).
 */

function resolveNames(item: ApiClientListItem): {
  type: IdentityType;
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
} {
  const identity = item.identity;
  const type: IdentityType = identity?.type ?? "individual";
  if (type === "business") {
    return {
      type,
      company_name: identity?.first_name?.trim() || null,
      first_name: null,
      last_name: null,
    };
  }
  return {
    type,
    company_name: null,
    first_name: identity?.first_name?.trim() || null,
    last_name: identity?.last_name?.trim() || null,
  };
}

export function normalizeClientListItem(item: ApiClientListItem): ClientListItem {
  const identity = item.identity;
  return {
    id: item.id,
    organization_id: item.organization_id,
    identity_id: item.identity_id ?? null,
    ...resolveNames(item),
    email: item.email ?? null,
    phone: item.phone ?? null,
    country: item.country ?? identity?.country ?? null,
    identification_type: identity?.identification_type ?? null,
    identification_number: identity?.identification_number ?? null,
    // The list query does not join `identity.kyc_provider` in QA, so this stays null there.
    kyc_status: identity?.kyc_provider?.status ?? null,
    created_at: item.created_at,
    updated_at: item.updated_at,
    deleted_at: item.deleted_at,
  };
}

export function normalizeClientDetail(item: ApiClientDetail): ClientByIdResponse {
  const identity = item.identity;
  const kyc = identity?.kyc_provider ?? null;
  return {
    ...normalizeClientListItem(item),
    birthdate: identity?.birthdate ?? null,
    incorporation_date: identity?.incorporation_date ?? null,
    sumsub_applicant_id: kyc?.provider_applicant_id ?? null,
    kyc_verified_at: kyc?.verified_at ?? null,
    kyc_rejection_reason: kyc?.rejection_reason ?? null,
    kyc_documents: kyc?.documents ?? null,
    accounts: item.accounts ?? [],
    // Only engine-issued links are surfaced. The provider's hosted link stored in
    // `kyc_provider.metadata.websdk_link` is never used as a fallback — it would show the provider's
    // own flow/URL to the user (rule: no provider identity on screen).
    activeKycLinkExpiresAt: item.activeKycLinkExpiresAt ?? null,
    activeKycLinkVerificationUrl: item.activeKycLinkVerificationUrl ?? null,
  };
}

/**
 * The client-profile endpoint already returns the flat client-detail shape; only the personal date fields
 * (never present for an organization) need defaulting to satisfy the view model.
 */
export function normalizeOrganizationClientProfile(item: ApiOrganizationClientProfile): ClientByIdResponse {
  return { ...item, identity_id: item.identity_id ?? null, birthdate: null, incorporation_date: null };
}

/** Legal name for businesses, "first last" for individuals — the create response has no identity joined. */
export function displayNameFromCreateBody(body: CreateClientBody): string {
  if (body.type === "business") {
    return (body.identity as BusinessIdentityRequest).legal_name?.trim() ?? "";
  }
  const identity = body.identity as IndividualIdentityRequest;
  return [identity.first_name?.trim(), identity.last_name?.trim()].filter(Boolean).join(" ");
}

/** Display name for a normalized client: legal name for businesses, "first last" for individuals. */
export function clientDisplayName(
  client: Pick<ClientListItem, "company_name" | "first_name" | "last_name" | "email" | "id">,
): string {
  const company = client.company_name?.trim();
  if (company) return company;
  const person = [client.first_name?.trim(), client.last_name?.trim()].filter(Boolean).join(" ");
  if (person) return person;
  return client.email ?? client.id;
}
